/* eslint-disable no-shadow */

import { Base } from "..";

/**
 * Раздел «dev-resolver-productivity-temp».
 *
 * Сервис только читает: данные собираются на бэкенде фоновыми утилитами и
 * лежат в Redis, здесь нет ни одной изменяющей операции.
 */
class Diagnostics extends Base {
	private static readonly options = { silent: false, name: "Diagnostics" };

	public static async getServices(): Promise<Diagnostics.ServicesResponse> {
		return this.request(
			(prpc) => prpc.theirsModel.diagnostics.getServices(),
			this.options,
		);
	}

	public static async getOverview(
		params: Diagnostics.OverviewParams = {},
	): Promise<Diagnostics.OverviewResponse> {
		return this.request(
			(prpc) => prpc.theirsModel.diagnostics.getOverview(params),
			this.options,
		);
	}

	public static async getTimeline(
		params: Diagnostics.TimelineParams = {},
	): Promise<Diagnostics.TimelineResponse> {
		return this.request(
			(prpc) => prpc.theirsModel.diagnostics.getTimeline(params),
			this.options,
		);
	}

	public static async getOperations(
		params: Diagnostics.OperationsParams = {},
	): Promise<Diagnostics.OperationsResponse> {
		return this.request(
			(prpc) => prpc.theirsModel.diagnostics.getOperations(params),
			this.options,
		);
	}

	public static async getIncidents(
		params: Diagnostics.IncidentsParams = {},
	): Promise<Diagnostics.IncidentsResponse> {
		return this.request(
			(prpc) => prpc.theirsModel.diagnostics.getIncidents(params),
			this.options,
		);
	}

	public static async getIncident(
		id: string,
	): Promise<Diagnostics.IncidentResponse> {
		return this.request(
			(prpc) => prpc.theirsModel.diagnostics.getIncident({ id }),
			this.options,
		);
	}

	public static async getClients(
		params: Diagnostics.ClientsParams = {},
	): Promise<Diagnostics.ClientsResponse> {
		return this.request(
			(prpc) => prpc.theirsModel.diagnostics.getClients(params),
			this.options,
		);
	}
}

namespace Diagnostics {
	export interface MetricPoint {
		ts: number;
		pod: string;
		rss: number;
		heapUsed: number;
		heapTotal: number;
		external: number;
		arrayBuffers: number;
		eventLoopDelayMean: number;
		eventLoopDelayMax: number;
		extra?: Record<string, number>;
	}

	export interface AllocationEntry {
		name: string;
		selfSize: number;
		file?: string;
	}

	export interface AllocationWindow {
		ts: number;
		pod: string;
		durationMs: number;
		totalSize: number;
		byModule: AllocationEntry[];
		byFunction: AllocationEntry[];
	}

	export interface CpuFunctionEntry {
		name: string;
		file?: string;
		selfMs: number;
		totalMs: number;
		samples: number;
	}

	export interface CpuStackEntry {
		frames: string[];
		selfMs: number;
		samples: number;
	}

	export interface CpuBlockEntry {
		ms: number;
		offsetMs: number;
		frames: string[];
	}

	export interface CpuProfileWindow {
		ts: number;
		pod: string;
		durationMs: number;
		samplingIntervalUs: number;
		sampleCount: number;
		totalMs: number;
		byFunction: CpuFunctionEntry[];
		byStack: CpuStackEntry[];
		blocks?: CpuBlockEntry[];
	}

	export interface OperationSummary {
		name: string;
		calls: number;
		errors: number;
		avgDuration: number;
		maxDuration: number;
	}

	export interface OperationDeviation extends OperationSummary {
		baselineCalls: number;
		deviationPercent: number | null;
	}

	export type IncidentReason = "threshold" | "growth" | "latency";

	export interface IncidentListItem {
		id: string;
		service: string;
		pod: string;
		ts: number;
		reason: IncidentReason;
		heapUsed: number;
		rss: number;
		topOperation: string | null;
	}

	export interface Incident {
		id: string;
		service: string;
		pod: string;
		ts: number;
		reason: IncidentReason;
		memory: {
			rss: number;
			heapUsed: number;
			heapTotal: number;
			external: number;
			arrayBuffers: number;
		};
		baseline: {
			rss: number;
			heapUsed: number;
			heapTotal: number;
		};
		eventLoopDelayMean: number;
		eventLoopDelayMax: number;
		extra?: Record<string, number>;
		stagingStats?: unknown;
		operations: OperationDeviation[];
		subscriptions: { name: string; count: number }[];
		allocations: AllocationWindow | null;
		cpuProfile?: CpuProfileWindow | null;
		snapshotObject?: string;
	}

	export interface ServicesResponse {
		items: { service: string; pods: string[] }[];
	}

	export interface OverviewParams {
		service?: string;
		rangeMinutes?: number;
	}

	export interface OverviewResponse {
		service: string;
		from: number;
		to: number;
		pods: {
			pod: string;
			latest: MetricPoint | null;
			heapUsedDelta: number;
			rssDelta: number;
			online: boolean;
			allocations: AllocationWindow | null;
		}[];
		recentIncidents: IncidentListItem[];
	}

	export interface TimelineParams {
		service?: string;
		from?: number;
		to?: number;
		maxPoints?: number;
	}

	export interface TimelineResponse {
		service: string;
		from: number;
		to: number;
		pods: { pod: string; points: MetricPoint[] }[];
		calls: { ts: number; calls: number; errors: number }[];
	}

	export interface OperationsParams {
		service?: string;
		from?: number;
		to?: number;
		limit?: number;
	}

	export interface OperationsResponse {
		service: string;
		from: number;
		to: number;
		total: number;
		items: OperationSummary[];
	}

	export interface IncidentsParams {
		service?: string;
		from?: number;
		to?: number;
		limit?: number;
	}

	export interface IncidentsResponse {
		from: number;
		to: number;
		items: IncidentListItem[];
	}

	export interface IncidentResponse {
		incident: Incident;
		context: MetricPoint[];
	}

	export interface ClientsParams {
		limit?: number;
	}

	export interface ClientSession {
		sessionId: string;
		remoteAddress: string;
		userType: string;
		userId: string;
		connectedAt: number;
		authenticatedAt: number | null;
		subscriptionCount: number;
		subscriptions: { name: string; count: number }[];
	}

	export interface ClientsResponse {
		pod: string;
		ts: number;
		totalClients: number;
		totalSubscriptions: number;
		items: ClientSession[];
	}
}

export default Diagnostics;
